import React from "react";
import { useUserControls } from "../../context/UserControlsProvider";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Collapse from "@mui/material/Collapse";
import { Checkbox, IconButton, ListItemButton, Chip, Box } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Divider from "@mui/material/Divider";
import { ItemSnapshot } from "../../types/DB2";
import { getStartingStatuses, getSuccessStatuses } from "../../util/util";
import { green, grey, red } from "@mui/material/colors";
import ListItemDetails from "./ListItemDetails";
import { OfflineSyncInfo } from "../../offline";
import { useOfflineStatusBadge, useOfflineSync } from "../../context/OfflineSyncProvider";
import { formatStatusLabel } from "../../utils/status";

type OfflineAwareItemSnapshot = ItemSnapshot & { offline?: OfflineSyncInfo };

interface CustomListItemProps {
  itemSnapshot: OfflineAwareItemSnapshot;
  index?: number;
  onOptionsClick: (
    event: React.MouseEvent<HTMLElement>,
    item: ItemSnapshot
  ) => void;
  onItemClick: (
    event: React.MouseEvent<HTMLElement>,
    item: ItemSnapshot
  ) => void;
  selected: boolean;
  onSelect: (item: ItemSnapshot, checked: boolean) => void;
  isExpanded: boolean;
}
const CustomListItem: React.FC<CustomListItemProps> = ({
  itemSnapshot,
  index,
  onOptionsClick,
  onItemClick,
  selected,
  onSelect,
  isExpanded,
}) => {
  const { loadingStatus } = useUserControls();
  const offlineBadge = useOfflineStatusBadge(itemSnapshot.offline?.status);
  const { syncNow } = useOfflineSync();
  const offlineStatus = itemSnapshot.offline?.status;
  const isRetryableOfflineStatus =
    offlineStatus === "pending" || offlineStatus === "error";
  const handleOfflineChipClick = React.useCallback(
    async (event: React.MouseEvent<HTMLButtonElement | HTMLDivElement>) => {
      if (!isRetryableOfflineStatus) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      const id = itemSnapshot.id ?? "unknown-item";
      const status = offlineStatus ?? "unknown";
      try {
        console.log("[OfflineRetry] User tapped item offline chip", {
          scope: "item",
          itemId: id,
          status,
        });
        await syncNow();
        console.log("[OfflineRetry] syncNow resolved after item chip tap", {
          itemId: id,
          status,
        });
      } catch (error) {
        console.error("[OfflineRetry] syncNow rejected after item chip tap", {
          itemId: id,
          status,
          error,
        });
      }
    },
    [isRetryableOfflineStatus, itemSnapshot.id, offlineStatus, syncNow]
  );
  const itemBgcolor = "white";
  const itemColor = grey[900];
  const successStatuses = getSuccessStatuses(loadingStatus);
  const startStatuses = getStartingStatuses(loadingStatus);
  const statusColor = (() => {
    if (successStatuses.includes(itemSnapshot.status)) {
      return green[700];
    } else if (startStatuses?.includes(itemSnapshot.status)) {
      return grey[600];
    }
    return red[400];
  })();
  return (
    <div key={itemSnapshot.id}>
      <ListItem
        sx={{
          ml: 0,
          minHeight: "1.5rem",
          py: 0.25,
          paddingLeft: "0",
          // width: `calc(100% - ${theme.spacing(4)})`, // full width minus left margin
          width: "100%", // full width minus left margin
          boxSizing: "border-box",
          paddingTop: 0,
          paddingBottom: 0,
          bgcolor: itemBgcolor,
          justifyContent: "space-between",
          borderBottom: `1px solid #e0e0e0`,
          borderLeft: "1px solid #e0e0e0",
          borderTop: index === 0 ? "1px solid #e0e0e0" : "none",
        }}
        secondaryAction={
          <IconButton
            edge="end"
            aria-label="item-menu"
            onClick={(e) => onOptionsClick(e, itemSnapshot)}
            size="small"
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        }
      >
        <ListItemButton
          role={undefined}
          onClick={(e) => onItemClick(e, itemSnapshot)}
          sx={{
            paddingRight: ".5rem !important",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingLeft: "3rem",
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <ListItemIcon sx={{ minWidth: 32 }}>
              <Checkbox
                edge="start"
                checked={selected}
                tabIndex={-1}
                disableRipple
                color="default"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(itemSnapshot, !selected);
                }}
              />
            </ListItemIcon>
            <ListItemText
              id={itemSnapshot.id}
              primary={
                itemSnapshot.cabinetNumber
                  ? `Cabinet # ${itemSnapshot.cabinetNumber}`
                  : `Item: ${itemSnapshot.barcodeId ?? itemSnapshot.id}`
              }
              color={itemColor}
              slotProps={{ primary: { sx: { color: itemColor, ml: 0.5 } } }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <ListItemText
              slotProps={{
                primary: {
                  sx: {
                    color: statusColor,
                    textAlign: "right",
                  },
                },
              }}
              id={itemSnapshot.id + itemSnapshot.status}
              primary={
                formatStatusLabel(itemSnapshot.status) ||
                itemSnapshot.status ||
                ""
              }
            />
            {offlineBadge && (
              isRetryableOfflineStatus ? (
                <Box
                  component="button"
                  type="button"
                  onClick={handleOfflineChipClick}
                  sx={{
                    ml: 1,
                    border: "none",
                    padding: 0,
                    background: "transparent",
                    display: "inline-flex",
                    cursor: "pointer",
                  }}
                  aria-label="Retry offline sync"
                  title={itemSnapshot.offline?.lastError}
                >
                  <Chip
                    label={offlineBadge.label}
                    color={offlineBadge.color as "success" | "warning" | "error"}
                    size="small"
                    variant={offlineBadge.color === "success" ? "outlined" : "filled"}
                    sx={{ pointerEvents: "none" }}
                  />
                </Box>
              ) : (
                <Chip
                  label={offlineBadge.label}
                  color={offlineBadge.color as "success" | "warning" | "error"}
                  size="small"
                  variant={offlineBadge.color === "success" ? "outlined" : "filled"}
                  sx={{ ml: 1 }}
                  title={itemSnapshot.offline?.lastError}
                />
              )
            )}
          </div>
        </ListItemButton>
      </ListItem>

      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <ListItemDetails itemSnapshot={itemSnapshot} />
        <Divider />
      </Collapse>
    </div>
  );
};

export default CustomListItem;
